import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { LoadingAndFeedbackState } from '../../../models/loading-and-feedback-state.type';
import dayjs from 'dayjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { IconComponent } from '../../icon/icon.component';
import { AutoHeightTextareaDirective } from '../../../directives/auto-height-textarea/auto-height-textarea.directive';
import { AutoInitialWidthInputFieldDirective } from '../../../directives/auto-initial-width-input-field/auto-initial-width-input-field.directive';
import { NgTemplateOutlet } from '@angular/common';
import { getFormattedNumber } from '../../../functions/get-formatted-number';
import { getMaxLengthFromFormControl } from '../../../functions/get-maxlength-from-form-control';

export type InputFieldVariant = 'textField' | 'textarea';

@Component({
  selector: 'work-ui-input-field',
  standalone: true,
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
  imports: [
    TranslateModule,
    LoadingSpinnerComponent,
    IconComponent,
    AutoHeightTextareaDirective,
    AutoInitialWidthInputFieldDirective,
    NgTemplateOutlet,
  ],
})
export class InputFieldComponent implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor, Validator, OnDestroy {
  @ViewChild('inputField') inputField: ElementRef | undefined;
  @ViewChild('inputArea') inputArea: ElementRef | undefined;
  @ViewChild('unitText') unitTextElement: ElementRef | undefined;
  // General config
  @Input() variant: InputFieldVariant = 'textField';
  @Input() id = '';
  @Input() name = '';
  @Input() label = '';
  @Input() labelSize: 'small' | 'default' = 'small';
  @Input() type = '';
  /**
   * `inputmode` des Feldes — steuert auf Mobilgeräten die eingeblendete Tastatur. Für rein
   * numerische Eingaben (PIN, Postleitzahl) `numeric`, ohne dass daraus ein `type="number"` mit
   * Spinner und Scroll-Verhalten wird.
   */
  @Input() inputMode: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search' | undefined;
  @Input() placeholder = '';
  @Input() value: any = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() readonlyFormatNumber = false;
  /**
   * Zahl (`type="number"`) ausserhalb des Fokus gut lesbar zeigen — mit Tausendertrennzeichen und fixen Nachkommastellen
   * (z. B. «1'234.50»). Beim Bearbeiten ist es ein normales Zahlenfeld; der Formularwert bleibt immer die Zahl.
   */
  @Input() formatNumber = false;
  @Input() formatNumberDecimals = 2;
  /** Anzeige ausserhalb des Fokus (siehe `formatNumber`); `null` = Rohwert zeigen. */
  protected formattedNumber: string | null = null;
  @Input() readonlyNoBorders = false;
  @Input() iconName = '';
  @Input() textFieldUnitName = '';
  @Input() iconRight = false;
  @Input() tableInput = false;
  @Input() editable = false;
  @Input() megaSearch = false;
  @Input() maxWidth: number | string | undefined;
  @Input() fitContentWidth = false;
  @Input() setAutoHeight = false;
  @Input() autoComplete = false;
  @Input() secretRevealToggle = false;
  // Validation
  @Input() required = false;
  @Input() invalid = false;
  @Input() errorMessage = '';
  @Input() hintMessage = '';
  @Input() disableError = false;
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() debounceTimeInMs = 500;
  @Input() formControlName = '';
  @Input() validateSwissPhoneNumbers = false;
  @Input() incrementStep: number | undefined = undefined;
  // Loading state
  @Input() showLoadingOnValueChange = false;
  @Input() loadingState: LoadingAndFeedbackState = 'none';
  @Input() showClearButton = false;
  /**
   * Tastenkürzel-Hinweis rechts im leeren Feld (z.B. `⌘ K`). Nur Anzeige — das Kürzel selbst
   * registriert der Aufrufer und fokussiert das Feld über `focus()`.
   */
  @Input() shortcutHint = '';
  @Output() valueChange = new EventEmitter<any>();
  @Output() loadingStateChange: EventEmitter<LoadingAndFeedbackState> = new EventEmitter<LoadingAndFeedbackState>();
  public currentCharLength = 0;
  public maxLength: number | undefined;
  public showMaxLengthCounter = false;
  public hasFocus = false;
  protected nrOfRowsDefault = 6;
  @Input() rows = this.nrOfRowsDefault;
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private controlContainer = inject(ControlContainer, { optional: true });
  private translateService = inject(TranslateService);
  private inputElement!: HTMLInputElement;
  private valueChangedSubject = new Subject<any>();
  private emailOrTelValueChangedSubject = new Subject<any>();
  private lastEmittedValue: any = undefined;
  private lastEmittedEmailOrTelValue: any = undefined;
  private controlStatusChangeSubscription: Subscription = new Subscription();
  private inputErrorMessage = '';
  protected formControl: FormControl | undefined;
  private isDateInput = false;
  private resizeObserver!: ResizeObserver;
  private initialType = '';
  private valueChangeSubscriber: Subscription | undefined = undefined;
  private emailOrTelValueChangedSubscriber: Subscription | undefined = undefined;
  private focusSubscriber: Subscription | undefined = undefined;
  private blurSubscriber: Subscription | undefined = undefined;

  ngOnInit() {
    if (!this.id && this.name) {
      this.id = this.name;
    }

    if (this.placeholder && this.type === 'date' && !this.value) {
      this.isDateInput = true;
      this.type = 'text';
    }

    if (this.fitContentWidth) {
      this.elementRef.nativeElement.style.width = 'auto';
    }

    this.initialType = this.type;

    this.setOptionsFromFormControl();

    if (this.maxLength === undefined) {
      if (this.variant === 'textarea' && this.max) {
        this.maxLength = this.max;
        this.showMaxLengthCounter = true;
      } else {
        // prevent maxLength = 0 assumption if variable is undefined
        this.maxLength = 10000;
      }
    }

    this.currentCharLength = this.value != null ? String(this.value).length : 0;

    // save initial hintMessage from parent for validation usage
    this.inputErrorMessage = this.errorMessage;

    if (this.type === 'email' || this.type === 'tel') {
      this.emailOrTelValueChangedSubscriber = this.emailOrTelValueChangedSubject
        .pipe(debounceTime(3 * this.debounceTimeInMs))
        .subscribe((value) => {
          if (value === this.lastEmittedEmailOrTelValue) return;
          this.lastEmittedEmailOrTelValue = value;
          this.validateNow();
          this.valueChange.emit(value);
        });
    } else {
      this.valueChangeSubscriber = this.valueChangedSubject.pipe(debounceTime(this.debounceTimeInMs)).subscribe((value) => {
        if (value === this.lastEmittedValue) return;
        this.lastEmittedValue = value;
        if (!this.required || value !== '') {
          if (this.showLoadingOnValueChange) {
            this.loadingState = 'loading';
            this.loadingStateChange.emit('loading');
          }
          if (this.initialType === 'number') {
            value = parseFloat(value);
          }
          this.valueChange.emit(value);
        }
      });
    }

    // Password
    if (this.initialType === 'password') {
      this.secretRevealToggle = true;
    } else if (this.secretRevealToggle) {
      // Initial toggle to hide the value (if no password)
      this.togglePasswordVisibility();
    }

    // Search
    if (this.type === 'search') {
      this.showClearButton = true;
    }
  }

  ngAfterViewInit() {
    this.inputElement = this.inputField?.nativeElement || this.inputArea?.nativeElement;
    if (this.placeholder && this.isDateInput && this.inputField) {
      setTimeout(() => {
        if (this.value) {
          this.value = dayjs(this.value).format('YYYY-MM-DD');
        }
      });
      this.focusSubscriber = this.inputField.nativeElement.addEventListener('focus', () => {
        if (!this.value && !this.readonly) {
          this.type = 'date';
        }
      });
      this.blurSubscriber = this.inputField.nativeElement.addEventListener('blur', () => {
        if (!this.value && !this.readonly) {
          this.type = 'text';
        }
      });
    }

    this.formatNumberValue();
    this.changeDetectorRef.detectChanges();

    // handle unitText resizing
    if (this.textFieldUnitName && this.unitTextElement) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const targetWidth = entry.target.clientWidth;
          if (this.inputField) {
            this.renderer.setStyle(this.inputField.nativeElement, 'padding-left', `${targetWidth + (this.readonly ? 8 : 16)}px`);
          }
        }
      });

      // Start observing the target element
      this.resizeObserver.observe(this.unitTextElement.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadingState'] && changes['loadingState'].currentValue) {
      switch (changes['loadingState'].currentValue as LoadingAndFeedbackState) {
        case 'loading': {
          this.loadingStateChange.emit('loading');
          break;
        }
        case 'success': {
          this.loadingStateChange.emit('success');
          break;
        }
        case 'error': {
          this.loadingStateChange.emit('error');
          break;
        }
        case 'none': {
          this.loadingStateChange.emit('none');
          break;
        }
      }
    }
    if (changes['readonly'] || changes['value']) {
      this.formatNumberValue();
    }
    if (changes['value'] || changes['formatNumber'] || changes['formatNumberDecimals']) {
      this.refreshFormattedNumber();
    }
    if (changes['value']) {
      this.lastEmittedValue = changes['value'].currentValue;
      this.lastEmittedEmailOrTelValue = changes['value'].currentValue;
    }
  }

  ngOnDestroy() {
    try {
      this.valueChangedSubject.unsubscribe();
      this.emailOrTelValueChangedSubject.unsubscribe();
      this.loadingStateChange.unsubscribe();
      this.controlStatusChangeSubscription.unsubscribe();
      this.resizeObserver.disconnect();
      this.emailOrTelValueChangedSubscriber?.unsubscribe();
      this.valueChangeSubscriber?.unsubscribe();
      this.focusSubscriber?.unsubscribe();
      this.blurSubscriber?.unsubscribe();
    } catch {
      // nothing to catch
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: any = () => {};

  protected updateValueFromNativeInput(): void {
    const inputValue = this.inputElement.value;
    this.currentCharLength = inputValue.length;
    let valueToEmit: any = this.type === 'number' || this.initialType === 'number' ? parseFloat(inputValue) : inputValue;

    /**
     * Den gebundenen Wert mit dem nativen Input synchron halten. Ohne das bleibt "value" auf dem zuletzt via writeValue
     * gesetzten Stand stehen — wird das Formular danach auf genau diesen Ausgangswert zurückgesetzt (z.B. leeres
     * Erfassungsformular nach dem Speichern), erkennt Angular keine Änderung der [value]-Bindung und der getippte
     * Text bleibt im Feld stehen.
     */
    this.value = inputValue;

    if (this.isDateInput && !inputValue) {
      this.value = null;
      valueToEmit = null;
    }

    this.onChange(valueToEmit);

    if (this.type === 'email' || this.type === 'tel') {
      this.emailOrTelValueChangedSubject.next(valueToEmit);
    } else {
      this.onTouched();
      this.validateNow();
      this.valueChangedSubject.next(valueToEmit);
    }
  }

  writeValue(value: any): void {
    if (value && this.type === 'datetime-local') {
      this.value = dayjs(value).format('YYYY-MM-DDTHH:mm');
    } else if (value && this.type === 'date') {
      this.value = dayjs(value).format('YYYY-MM-DD');
    } else {
      this.value = value;
    }
    if (value && this.isDateInput) {
      this.type = 'date';
    }
    this.lastEmittedValue = this.value;
    this.lastEmittedEmailOrTelValue = this.value;
    this.currentCharLength = this.value != null ? String(this.value).length : 0;
    this.refreshFormattedNumber();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Used for real-time validation while user is typing rather
   * than only triggering when focus is lost on input
   */
  validateNow() {
    if (this.formControl) {
      this.formControl.markAsTouched();
      this.validateInput();
    }
  }

  validateInput() {
    if (this.formControl) {
      const value = this.formControl.value;

      if (this.formControl.touched) {
        // Check if the input is required and is empty
        if (value === null || value === undefined || value === '') {
          if (this.required) {
            this.invalid = true;
            this.errorMessage = 'FELD_DARF_NICHT_LEER_SEIN';
            return { required: true };
          }
          return null; // If not required, and value is empty, it's valid
        }

        // Min/Max validation
        if (this.min !== undefined && value < this.min) {
          this.invalid = true;
          this.errorMessage = this.inputErrorMessage
            ? this.inputErrorMessage
            : this.translateService.instant('WERT_MUSS_MINDESTENS_X_SEIN', { min: this.min });
          return { min: { min: this.min, actual: value } };
        }
        if (this.max !== undefined && value > this.max) {
          this.invalid = true;
          this.errorMessage = this.inputErrorMessage
            ? this.inputErrorMessage
            : this.translateService.instant('WERT_DARF_MAXIMAL_X_SEIN', { max: this.max });
          return { max: { max: this.max, actual: value } };
        }

        // take native max Length from FormControl
        if (this.formControl.errors && Object.prototype.hasOwnProperty.call(this.formControl.errors, 'maxlength')) {
          // fallback: if upfront extraction missed it, capture from the error
          const error = this.formControl.errors['maxlength'];
          this.maxLength = error['requiredLength'];
          this.showMaxLengthCounter = true;
        }

        // Maxlength validation
        if (this.maxLength !== undefined && value.length > this.maxLength) {
          this.invalid = true;
          this.errorMessage = this.translateService.instant('MAXIMALE_LAENGE_X_ZEICHEN_ERREICHT', { maxLength: this.maxLength });
          return { maxlength: { requiredLength: this.maxLength, actualLength: value.length } };
        }

        // Email validation
        if (this.type === 'email' && !this.isValidEmail(value)) {
          this.invalid = true;
          this.errorMessage = 'GUELTIGE_EMAIL_ADRESSE_EINGEBEN';
          return { email: true };
        }

        // Phone validation
        if (this.type === 'tel' && this.validateSwissPhoneNumbers && !this.isValidSwissPhoneNumber(value)) {
          this.invalid = true;
          this.errorMessage = 'GUELTIGE_TELEFONNUMMER_EINGEBEN';
          return { tel: true };
        }
      }
    }

    // Reset invalid state
    this.invalid = false;

    // If the value passes all validations
    return null;
  }

  validate(): ValidationErrors | null {
    return this.validateInput();
  }

  onBlur() {
    this.hasFocus = false;
    this.onTouched();
    this.validateNow();
    this.refreshFormattedNumber();
  }

  onFocus() {
    this.hasFocus = true;
    this.refreshFormattedNumber();
  }

  /**
   * Browser ändern den Wert eines fokussierten `type="number"`-Felds beim Scrollen mit dem Mausrad —
   * in Tabellen (z.B. Menge in der Kalkulation) werden so unbemerkt Werte verändert. Das Feld verliert
   * deshalb den Fokus, bevor der Browser den Wert anpasst; die Seite scrollt normal weiter.
   */
  onWheel(event: WheelEvent) {
    const target = event.target as HTMLInputElement;
    if (target.type === 'number' && document.activeElement === target) {
      target.blur();
    }
  }

  /**
   * Clear the input.
   */
  clear() {
    this.value = '';
    this.onChange(this.value);
    this.validateNow();
    // valueChangesubject will trigger the valueChange.emit
    this.valueChangedSubject.next(this.value);
    this.inputElement.focus();
  }

  /**
   * Fokussiert das Feld und markiert den bestehenden Inhalt, damit direkt überschrieben werden kann.
   */
  public focus() {
    this.inputElement?.focus();
    this.inputElement?.select();
  }

  /**
   * Toggle visibility for passwords.
   */
  public togglePasswordVisibility() {
    if (this.type === 'password' && this.initialType !== 'password') {
      this.type = this.initialType;
    } else if (this.type === 'password' && this.initialType === 'password') {
      this.type = 'text';
    } else {
      this.type = 'password';
    }
  }

  private setOptionsFromFormControl() {
    if (this.controlContainer && this.formControlName) {
      this.formControl = this.controlContainer.control?.get(this.formControlName) as FormControl;
      if (this.formControl) {
        if (this.formControl.validator) {
          const validator = this.formControl.validator({} as AbstractControl);
          this.required = validator && validator['required'];
        }
        const maxLengthFromValidator = getMaxLengthFromFormControl(this.formControl);
        if (maxLengthFromValidator !== undefined) {
          this.maxLength = maxLengthFromValidator;
          this.showMaxLengthCounter = true;
        }
        this.controlStatusChangeSubscription = this.formControl.statusChanges.subscribe(() => {
          if (this.readonly && this.readonlyFormatNumber) {
            this.formatNumberValue();
          }
          this.required = this.formControl ? this.formControl.hasValidator(Validators.required) : false;
        });
      }
    }
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  private isValidSwissPhoneNumber(value: string): boolean {
    const phonePattern = /(\b(0041|0)|\B\+41)(\s?\(0\))?(\s)?[1-9]{2}(\s)?[0-9]{3}(\s)?[0-9]{2}(\s)?[0-9]{2}\b/;
    return phonePattern.test(value);
  }

  /** Formatierte Anzeige für `formatNumber` — nur ohne Fokus und nur für eine gültige Zahl. */
  private refreshFormattedNumber(): void {
    const numericValue = this.value === '' || this.value === null || this.value === undefined ? NaN : Number(this.value);
    this.formattedNumber =
      this.formatNumber && !this.hasFocus && Number.isFinite(numericValue)
        ? getFormattedNumber(numericValue, this.formatNumberDecimals)
        : null;
  }

  private formatNumberValue() {
    if (this.type === 'number' && this.readonlyFormatNumber && this.inputElement) {
      if (this.readonly) {
        this.inputElement.type = 'text';
        this.value = getFormattedNumber(this.value || 0);
      } else {
        this.value = parseFloat(this.value.replace(/'/g, '')) || '';
        this.inputElement.type = 'number';
      }
    }
  }
}
